import React, { JSX } from "react";
import { useDispatch } from "react-redux";
import { push } from "connected-react-router";
import { Box, Divider, Popover, Typography } from "@mui/material";

import { Bed } from "./Bed";
import { tl } from "../../../components/translate";
import DischargeForm from "../Discharge/DischargeForm";
import { TransferBedDialog } from "./TransferBedDialog";
import DischargeDetails from "../Discharge/DischargeDetails";
import LabRequestForm, { RequestType } from "../LabRequest/LabRequestForm";
import { saveEmergencyTriagePriority } from "../../../slices/bedManagementSlice";
import { IPD, TriagePriority } from "../../../interfaces/IpdInterface";
import { IpdAdmissionCreateEdit } from "../IpdAdmission/IpdAdmissionCreateEdit";
import { usePatchBed } from "../../../hooks/query/useBeds";
import { TWardType } from "../../../interfaces/WardInterface";
import { IpdInfoPanel } from "../IpdInfoPanel";
import IpdProgressRecordCreateEdit from "../IpdProgress/IpdProgressRecordCreateEdit";
import { formatIpdToExtended } from "../IpdList";
import { ExtendedBed } from "../../../hooks/useWardTypeData";
import { WardType } from "../../../constants/ward";
import { Room } from "../../../interfaces/RoomInterface";

// eslint-disable-next-line import/prefer-default-export
export const Beds = ({
  filteredBeds,
  triagePriorityMap,
  wardType,
  rooms = []
}: {
  filteredBeds: ExtendedBed[];
  triagePriorityMap: Record<number, TriagePriority>;
  wardType: TWardType;
  rooms: Array<Room>;
}): JSX.Element => {
  const dispatch = useDispatch();

  const { mutate: patchBed } = usePatchBed();

  const [showIpdInfo, setShowIpdInfo] = React.useState(false);
  const [activeIpd, setActiveIpd] = React.useState<IPD | null>(null);
  const [openDischargeForm, setOpenDischargeForm] = React.useState(false);
  const [showAdmissionPanel, setShowAdmissionPanel] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState<SVGSVGElement | null>(null);
  const [showBedTransferModal, setShowBedTransferModal] = React.useState(false);
  const [openDischargeDetails, setOpenDischargeDetails] = React.useState(false);
  const [selectedBed, setSelectedBed] = React.useState<ExtendedBed | null>(null);
  const [showProgressPanel, setShowProgressPanel] = React.useState<boolean>(false);

  const client = activeIpd?.client ?? null;

  const [labRequestFormInfo, setLabRequestFormInfo] = React.useState({
    open: false,
    type: RequestType.SERVICE
  });

  const closeAllPanel = () => {
    setShowProgressPanel(false);
    setOpenDischargeForm(false);
    setShowAdmissionPanel(false);
  };

  return (
    <>
      <Box
        sx={{
          width: "100%",
          p: 2,
          display: "flex",
          flexDirection: "column",
          gap: 2
        }}
      >
        <Typography sx={{ fontWeight: 600, fontSize: "18px" }}>{tl("ipd.bedsOverview")}</Typography>

        <Box
          sx={{
            display: "grid",
            gap: 2,
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, 1fr)",
              md: "repeat(4, 1fr)",
              lg: "repeat(6, 1fr)",
              xl: "repeat(8, 1fr)"
            }
          }}
        >
          {filteredBeds?.map((bed) => (
            <Box key={bed.id} data-testmation="bedCard">
              <Bed
                rooms={rooms}
                bed={bed}
                patchBed={patchBed}
                triagePriorityMap={triagePriorityMap}
                onMoreIconClick={({ e, activeBed, activeIpdOnBed }) => {
                  setAnchorEl(e.currentTarget);
                  setSelectedBed(activeBed);
                  setActiveIpd(activeIpdOnBed);
                  closeAllPanel();
                }}
              />
            </Box>
          ))}
        </Box>
      </Box>

      {/* admit popover */}
      <Popover
        disableScrollLock
        anchorEl={anchorEl}
        open={!!anchorEl}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center"
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left"
        }}
        onClose={() => {
          setActiveIpd(null);
          setSelectedBed(null);
          setAnchorEl(null);
        }}
      >
        <Box py={1} minWidth="180px">
          {!selectedBed?.isOccupied && (
            <>
              <Typography
                data-testmation="admitOption"
                px={2}
                py={1}
                sx={{ cursor: "pointer" }}
                onClick={() => {
                  setAnchorEl(null);
                  setShowAdmissionPanel(true);
                }}
              >
                Admit
              </Typography>
            </>
          )}

          {selectedBed?.isOccupied && (
            <>
              <Typography
                data-testmation="createBillOption"
                px={2}
                py={1}
                sx={{ cursor: "pointer" }}
                onClick={() => {
                  if (client?.id && selectedBed?.id) {
                    dispatch(
                      push(
                        `/billing/ipd?clientId=${client.id}&bedId=${selectedBed.id}&ipdId=${selectedBed?.activeIpd?.id}`
                      )
                    );
                  }
                }}
              >
                Create Bill
              </Typography>

              <Typography
                data-testmation="createProformaBillOption"
                px={2}
                py={1}
                sx={{ cursor: "pointer" }}
                onClick={() => {
                  if (client?.id && selectedBed?.id) {
                    dispatch(
                      push(
                        `/billing/ipd?clientId=${client.id}&bedId=${selectedBed.id}&ipdId=${selectedBed?.activeIpd?.id}&isProformaBill=true`
                      )
                    );
                  }
                }}
              >
                Create Proforma Bill
              </Typography>

              <Divider />

              <Typography
                data-testmation="transferBedOption"
                px={2}
                py={1}
                sx={{ cursor: "pointer" }}
                onClick={() => {
                  setAnchorEl(null);
                  setShowBedTransferModal(true);
                }}
              >
                Bed Transfer
              </Typography>

              <Typography
                data-testmation="progressRecordOption"
                px={2}
                py={1}
                sx={{ cursor: "pointer" }}
                onClick={() => {
                  setAnchorEl(null);
                  setShowProgressPanel(true);
                }}
              >
                Progress Record
              </Typography>

              <Typography
                data-testmation="labRequestOption"
                px={2}
                py={1}
                sx={{ cursor: "pointer" }}
                onClick={() => {
                  setAnchorEl(null);
                  setLabRequestFormInfo({
                    open: true,
                    type: RequestType.SERVICE
                  });
                }}
              >
                Lab Request Form
              </Typography>

              {/* triage setup */}
              {wardType === WardType.EMERGENCY && (
                <>
                  <Typography px={2} pt={1} fontSize="11px" color="text.secondary">
                    Set Triage Priority
                  </Typography>
                  {[
                    { label: "Critical", value: TriagePriority.Critical, color: "#e03030" },
                    { label: "Urgent", value: TriagePriority.Urgent, color: "#e8a020" },
                    { label: "Normal", value: TriagePriority.Normal, color: "#1a7a4a" }
                  ].map(({ label, value, color }) => (
                    <Typography
                      key={value}
                      px={2}
                      py={0.8}
                      sx={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 1 }}
                      onClick={() => {
                        if (selectedBed?.activeIpd?.id) {
                          dispatch(
                            saveEmergencyTriagePriority({
                              ipdId: selectedBed.activeIpd.id,
                              priority: value
                            })
                          );
                        }
                        setAnchorEl(null);
                      }}
                    >
                      <Box
                        sx={{
                          width: "10px",
                          height: "10px",
                          backgroundColor: color,
                          borderRadius: "50%",
                          flexShrink: 0
                        }}
                      />
                      {label}
                    </Typography>
                  ))}
                </>
              )}

              <Divider />

              <Typography
                data-testmation="dischargeOption"
                px={2}
                py={1}
                sx={{ cursor: "pointer" }}
                onClick={() => {
                  setAnchorEl(null);
                  setOpenDischargeForm(true);
                }}
              >
                Discharge
              </Typography>
            </>
          )}
        </Box>
      </Popover>

      {/* panels */}
      {showAdmissionPanel && selectedBed && (
        <IpdAdmissionCreateEdit
          onClose={() => setShowAdmissionPanel(false)}
          openedFromBed
          initialBedAssignment={{
            bedId: selectedBed.id,
            roomId: selectedBed.roomId,
            wardId: selectedBed.room.wardId
          }}
          onCreateSuccess={(createdIpd) => {
            setShowAdmissionPanel(false);
            setShowIpdInfo(true);
            setSelectedBed((prev) => {
              if (!prev) return prev;
              return {
                ...prev,
                isOccupied: true,
                activeIpd: createdIpd
              };
            });
          }}
        />
      )}

      {showIpdInfo && selectedBed?.activeIpd?.id && (
        <IpdInfoPanel
          ipd={{
            ...formatIpdToExtended(selectedBed.activeIpd)
          }}
          onClose={() => setShowIpdInfo(false)}
          onDischargeFormOpen={() => null}
        />
      )}

      {showBedTransferModal && selectedBed && (
        <TransferBedDialog
          initialBed={selectedBed}
          open={showBedTransferModal}
          setOpen={setShowBedTransferModal}
        />
      )}

      {labRequestFormInfo.open && selectedBed?.activeIpd && (
        <LabRequestForm
          onClose={() => {
            setLabRequestFormInfo({
              ...labRequestFormInfo,
              open: false
            });
          }}
          requestType={labRequestFormInfo.type}
          ipd={{
            ...selectedBed.activeIpd,
            bedCode: selectedBed?.code
          }}
        />
      )}

      {openDischargeForm && selectedBed?.activeIpd && (
        <DischargeForm
          ipdData={selectedBed?.activeIpd}
          onClose={() => setOpenDischargeForm(false)}
          onDischargeSuccess={() => null}
        />
      )}

      {openDischargeDetails && selectedBed?.activeIpd?.id && (
        <DischargeDetails
          ipdData={selectedBed.activeIpd}
          onCancel={() => setOpenDischargeDetails(false)}
        />
      )}

      {showProgressPanel && selectedBed?.activeIpd && (
        <IpdProgressRecordCreateEdit
          onCancel={() => setShowProgressPanel(false)}
          ipdRecord={selectedBed?.activeIpd}
          afterSave={(ipd) => {
            setShowProgressPanel(false);
            setShowIpdInfo(true);
            setSelectedBed((prev) => {
              if (!prev) return prev;
              return {
                ...prev,
                activeIpd: ipd
              };
            });
          }}
        />
      )}
    </>
  );
};
